import { useEffect, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { useI18n } from '../i18n';
import { getMeta, setMeta } from '../db/db';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Sugerencia de instalación, mostrada solo cuando el usuario ya tiene hábitos (F-01). */
export function InstallBanner() {
  const { t } = useI18n();
  const [, force] = useState(0);
  const [dismissed, setDismissed] = useState(true);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    void (async () => {
      const until = await getMeta<number>('installDismissedUntil', 0);
      const hasHabits = await getMeta<boolean>('hasCreatedHabit', false);
      setDismissed(!hasHabits || until > Date.now());
    })();
    return () => {
      listeners.delete(l);
    };
  }, []);
  if (dismissed || isStandalone()) return null;
  const ios = isIos();
  if (!deferred && !ios) return null;
  const dismiss = () => {
    setDismissed(true);
    void setMeta('installDismissedUntil', Date.now() + 14 * 86_400_000);
  };
  return (
    <div className="banner banner--info" role="region" aria-label={t('today.installTitle')}>
      <Smartphone size={22} aria-hidden="true" />
      <div className="banner__body">
        <strong>{t('today.installTitle')}</strong>
        <span className="small">{ios ? t('today.installIosBody') : t('today.installBody')}</span>
        <div className="row">
          {deferred && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={async () => {
                await deferred?.prompt();
                deferred = null;
                dismiss();
              }}
            >
              {t('today.installAction')}
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--sm" onClick={dismiss}>
            {t('today.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
